import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import api, { REQUIRED_API_VERSION, SALON_NAME } from '../../services/api';
import { demoPosPath } from '../../demoPosPaths';
import BranchMultiSelect from '../branch/BranchMultiSelect';
import { BillsIcon, ChartIcon, CloseIcon, MenuIcon, QueueIcon } from '../common/icons';

const NAV = [
  { segment: '', label: "Today's queue", icon: QueueIcon, end: true },
  { segment: 'bills', label: 'Bills', icon: BillsIcon },
  { segment: 'analytics', label: 'Analytics', icon: ChartIcon },
];

const LOGO = `${process.env.PUBLIC_URL || ''}/engagement/enterprise_nxt_logo_hd.png`;
const RESTART_COMMAND = 'cd nyufaz-analyze/backend\npython naturals/app_demo_pos.py';

function BackendBanner() {
  const [state, setState] = useState('checking');
  useEffect(() => {
    api
      .health()
      .then((h) => {
        const v = h && typeof h === 'object' ? h.api_version : undefined;
        if (typeof v !== 'number') setState('down');
        else setState(v >= REQUIRED_API_VERSION ? 'ok' : 'outdated');
      })
      .catch(() => setState('down'));
  }, []);
  if (state === 'ok' || state === 'checking') return null;
  return (
    <div className="dp-notice dp-notice--error dp-banner">
      <div>
        <strong>
          {state === 'outdated'
            ? 'The POS backend is running older code.'
            : 'The POS backend is not reachable (or /demo-pos-api is not proxied to port 8012).'}
        </strong>{' '}
        Start it, then reload this page.
        <pre>{RESTART_COMMAND}</pre>
      </div>
    </div>
  );
}

export default function Shell({ children }) {
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setNavOpen(false), [location.pathname]);
  useEffect(() => {
    document.title = `${SALON_NAME} — POS`;
  }, []);

  return (
    <div className="dp-app">
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            fontFamily: "'Montserrat', sans-serif",
            fontSize: 13,
            background: '#2a1433',
            color: '#fff6e8',
            border: '1px solid rgba(247, 210, 153, 0.35)',
          },
          success: { iconTheme: { primary: '#f7d299', secondary: '#2a1433' } },
        }}
      />
      <div className={`dp-nav-backdrop${navOpen ? ' is-open' : ''}`} onClick={() => setNavOpen(false)} aria-hidden />
      <aside className={`dp-sidebar${navOpen ? ' is-open' : ''}`} aria-label="POS navigation">
        <div className="dp-brand">
          <button type="button" className="dp-icon-btn dp-sidebar-close" onClick={() => setNavOpen(false)} aria-label="Close menu">
            <CloseIcon />
          </button>
          <img src={LOGO} alt={SALON_NAME} className="dp-brand-logo" />
          <span className="dp-brand-tag">Point of Sale</span>
        </div>
        <nav className="dp-nav">
          {NAV.map(({ segment, label, icon: Icon, end }) => (
            <NavLink
              key={segment || 'home'}
              to={demoPosPath(segment)}
              end={end}
              className={({ isActive }) => `dp-nav-item${isActive ? ' active' : ''}`}
            >
              <Icon size={18} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="dp-sidebar-foot">
          <span className="dp-avatar">{SALON_NAME.slice(0, 1)}</span>
          <div>
            <strong>{SALON_NAME}</strong>
            <small>Salon billing · demo</small>
          </div>
        </div>
      </aside>

      <div className="dp-stage">
        <header className="dp-topbar">
          <button type="button" className="dp-icon-btn dp-menu-btn" onClick={() => setNavOpen(true)} aria-label="Open menu">
            <MenuIcon />
          </button>
          <BranchMultiSelect />
        </header>
        <main className="dp-main">
          <BackendBanner />
          {children ?? <Outlet />}
        </main>
      </div>
    </div>
  );
}
